import React from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight } from "lucide-react";

/**
 * Sponsor CTA — big, bold, impossible-to-miss pitch for businesses.
 * Drops wherever we want to convert foot-traffic into sponsor dollars.
 */
export default function SponsorCTA({ compact = false }) {
  return (
    <section
      className="relative overflow-hidden rounded-[2px] border"
      style={{
        background:
          "linear-gradient(135deg, #0F0F0F 0%, #1a0f0f 50%, #0F0F0F 100%)",
        borderColor: "#D7A62A",
        boxShadow:
          "0 0 0 1px #D7A62A, 0 0 40px -8px rgba(215,166,42,0.45), inset 0 0 60px rgba(215,166,42,0.06)",
      }}
      data-testid="sponsor-cta"
    >
      {/* Scanline texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)",
        }}
      />
      {/* Corner pill */}
      <div
        className="absolute top-0 right-0 px-3 py-1 font-mono text-[10px] uppercase tracking-widest"
        style={{
          color: "#0F0F0F",
          background: "#D7A62A",
          letterSpacing: "0.22em",
        }}
      >
        <Zap size={10} className="inline -mt-0.5 mr-1" />
        For Brands
      </div>

      <div
        className={`relative grid md:grid-cols-[1.4fr_1fr] gap-8 items-center ${compact ? "px-6 md:px-10 py-10" : "px-6 md:px-14 py-14 md:py-20"}`}
      >
        <div className="space-y-5">
          <h2
            className={`font-display uppercase leading-[0.9] ${
              compact
                ? "text-[clamp(1.8rem,4.5vw,3rem)]"
                : "text-[clamp(2.2rem,6vw,4.5rem)]"
            }`}
            style={{ color: "#F5F2E8" }}
            data-testid="sponsor-cta-heading"
          >
            Put Your Brand <br />
            Where The{" "}
            <span
              style={{
                color: "#D7A62A",
                textShadow:
                  "0 0 12px rgba(215,166,42,0.55), 0 0 32px rgba(215,166,42,0.25)",
              }}
            >
              Action
            </span>{" "}
            Is.
          </h2>
          <p
            className="text-base md:text-lg leading-relaxed max-w-xl"
            style={{ color: "rgba(245,242,232,0.78)" }}
            data-testid="sponsor-cta-subhead"
          >
            Join the movement that's getting Tennessee off the couch and into the
            community. Sponsor a <strong style={{ color: "#E53935" }}>Cash Drop</strong>,
            a <strong style={{ color: "#D7A62A" }}>Loot Quest</strong>, or a
            {" "}<strong style={{ color: "#76B82A" }}>$50/mo Branded Waymark</strong>{" "}
            (permanent pin at your door with a coupon built in). A billboard could
            never do this.
          </p>
          <div className="flex items-center gap-3 pt-2 flex-wrap">
            <Link
              to="/sponsor-apply"
              className="btn-loot inline-flex items-center gap-2 text-base"
              data-testid="sponsor-cta-button"
            >
              Sponsor a Drop <ArrowRight size={16} />
            </Link>
            <Link
              to="/sponsors"
              className="btn-ghost inline-flex items-center text-xs"
              data-testid="sponsor-cta-learn"
            >
              See who's already sponsoring →
            </Link>
          </div>
        </div>

        {/* Proof-point stack */}
        <div className="space-y-2">
          {[
            ["100%", "Physical presence — not ad fatigue"],
            ["0.5%", "What a typical Facebook ad gets you"],
            ["IRL", "Immediate foot traffic, real faces"],
          ].map(([k, v]) => (
            <div
              key={k}
              className="flex items-center gap-4 p-4 border"
              style={{
                borderColor: "rgba(215,166,42,0.35)",
                background: "rgba(215,166,42,0.04)",
              }}
            >
              <span
                className="font-display text-3xl md:text-4xl"
                style={{ color: "#D7A62A", minWidth: "4.5rem" }}
              >
                {k}
              </span>
              <span className="font-mono text-xs md:text-sm text-[#F5F2E8]/80 uppercase tracking-wider">
                {v}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
